import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { environment } from 'src/environments/environment';
import { LoginBaseComponent } from '../login-base.component';

/**
 * Primary login page — email only.
 * Eligible email → redirect straight to SAML IdP (skip Keycloak login screen).
 *
 * Logo and cover image loading are handled by LoginBaseComponent.
 */
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false
})
export class LoginComponent extends LoginBaseComponent implements OnInit {
  email = '';
  emailError = '';
  errorMessage = '';
  isSubmitting = false;

  private returnUrl = '/dashboard';
  private readonly emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  constructor(
    private readonly keycloak: KeycloakService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly http: HttpClient
  ) {
    super();
  }

  ngOnInit(): void {
    void this.bootstrap();
    this.loadAssets();
  }

  get canSubmit(): boolean {
    return this.isEmailValid(this.email.trim());
  }

  get submitLabel(): string {
    return this.isSubmitting ? 'Signing in…' : 'Continue with SSO';
  }

  onEmailBlur(): void {
    const value = this.email.trim();
    if (!value) {
      this.emailError = '';
      return;
    }
    if (!this.isEmailValid(value)) {
      this.emailError = 'Please enter a valid email address.';
    }
  }

  onEmailInput(): void {
    if (this.emailError && this.isEmailValid(this.email.trim())) {
      this.emailError = '';
    }
  }

  private isEmailValid(value: string): boolean {
    return this.emailPattern.test(value);
  }

  private async bootstrap(): Promise<void> {
    if (this.keycloak.isLoggedIn()) {
      await this.router.navigateByUrl(this.returnUrl);
      return;
    }

    const requested = this.route.snapshot.queryParamMap.get('returnUrl');
    if (requested && requested.startsWith('/')) {
      this.returnUrl = requested;
    }
  }

  async submit(): Promise<void> {
    this.errorMessage = '';

    const email = this.email.trim();
    if (!email) {
      this.emailError = 'Email address is required.';
      return;
    }
    if (!this.isEmailValid(email)) {
      this.emailError = 'Please enter a valid email address.';
      return;
    }
    this.emailError = '';

    this.isSubmitting = true;
    try {
      const isEligible = await this.checkEligibility(email);
      if (!isEligible) {
        return;
      }

      await this.keycloak.login({
        redirectUri: window.location.origin + this.returnUrl,
        idpHint: resolveSamlIdpAlias(environment),
        loginHint: email
      });
    } catch (_err) {
      this.errorMessage = 'Sign-in failed. Please try again in a moment.';
    } finally {
      this.isSubmitting = false;
    }
  }

  private async checkEligibility(email: string): Promise<boolean> {
    const eligibility = await firstValueFrom(
      this.http.get<{ eligible: boolean }>(
        `${environment.apiHost}/auth/login-eligibility`,
        { params: { email } }
      )
    );

    if (!eligibility?.eligible) {
      this.errorMessage =
        'Email not authorized. Contact your administrator for access.';
      return false;
    }

    return true;
  }
}

function resolveSamlIdpAlias(env: { samlIdpAlias?: string }): string {
  return env.samlIdpAlias || 'JioLoyaltySIT';
}
